import { ApiError, useAuthMeRetrieve } from "@agencyos/api-client";

import { useSession } from "./session";

export const SETTINGS_ROLES = ["owner", "manager"] as const;

/** One membership of the signed-in person, as the API describes it (D-120: position, modules, permissions). */
export interface CurrentMembership {
  organization_slug: string;
  role: string;
  languages?: readonly string[];
  /** Historical role per module ({ crm: "advisor" }, or { "*": "owner" }). */
  modules?: Readonly<Record<string, string>>;
  permissions?: readonly string[];
}

/** D-120: the back-office module (Django app) behind each route, so a position acts as the right
 * historical role on each screen. The most specific prefix comes first. */
const ROUTE_MODULES: readonly (readonly [prefix: string, module: string])[] = [
  ["/settings/team", "accounts"],
  ["/settings/integrations", "integrations"],
  ["/settings/documents", "documents"],
  ["/settings/billing", "billing"],
  ["/settings/contracts", "contracts"],
  ["/settings/growth", "crm"],
  ["/settings/notifications", "notifications"],
  ["/settings/audit", "core"],
  ["/settings", "agency"],
  ["/reception", "appointments"],
  ["/agenda", "appointments"],
  ["/inbox", "inbox"],
  ["/calls", "calls"],
  ["/leads", "crm"],
  ["/clients", "crm"],
  ["/dossiers", "immigration"],
  ["/contracts", "contracts"],
  ["/invoices", "billing"],
  ["/cash", "billing"],
  ["/catalog", "catalog"],
  ["/website", "website"],
  ["/studio", "studio"],
  ["/ads", "ads"],
  ["/academy-marketing", "marketing"],
  ["/academy-admin", "academy"],
  ["/social", "social"],
  ["/support", "support"],
  ["/bible", "knowledge"],
  ["/ai-test", "assistant"],
];

export function moduleOf(pathname: string): string {
  return ROUTE_MODULES.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`))?.[1] ?? "";
}

/** The historical role a membership has in a module ("" = none there). */
export function roleIn(membership: CurrentMembership | undefined, module: string): string | undefined {
  if (!membership) return undefined;
  const modules = membership.modules;
  if (!modules) return membership.role; // an API older than D-120
  return modules[module] ?? modules["*"] ?? "";
}

export function useCurrentMembership(): CurrentMembership | undefined {
  const me = useAuthMeRetrieve();
  const { organization } = useSession();
  if (me.data?.status !== 200) return undefined;
  const memberships = me.data.data.memberships as unknown as readonly CurrentMembership[];
  return memberships.find((m) => m.organization_slug === organization);
}

/** The position (poste) of the signed-in user in the current agency (D-120). */
export function usePosition(): string | undefined {
  return useCurrentMembership()?.role;
}

/** Role of the signed-in user in the current agency, for the module of the screen (D-120: a
 * position acts as one of the historical roles, module by module). */
export function useCurrentRole(module?: string): string | undefined {
  const membership = useCurrentMembership();
  const where = module ?? moduleOf(typeof window === "undefined" ? "" : window.location.pathname);
  return roleIn(membership, where);
}

/** A fine permission of the position (D-120), e.g. « academy.students.manage ». */
export function useHasPermission(code: string): boolean {
  const membership = useCurrentMembership();
  if (!membership) return false;
  if (!membership.permissions) return membership.role === "owner"; // an API older than D-120
  return membership.permissions.includes("*") || membership.permissions.includes(code);
}

/** Owner and manager edit the agency settings; everybody else reads them. */
export function useCanEditSettings(): boolean {
  const role = useCurrentRole();
  return role !== undefined && (SETTINGS_ROLES as readonly string[]).includes(role);
}

/** DRF validation errors → { field: first message } (already translated by the API). */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError) || error.status !== 400) return {};
  const body = error.body as Record<string, unknown> | null;
  if (!body || typeof body !== "object") return {};
  const out: Record<string, string> = {};
  for (const [field, value] of Object.entries(body)) {
    const first = Array.isArray(value) ? value[0] : value;
    if (typeof first === "string") out[field] = first;
    else if (first && typeof first === "object") {
      const nested = Object.values(first as Record<string, unknown>).flat()[0];
      if (typeof nested === "string") out[field] = nested;
    }
  }
  return out;
}
